import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Mail, Phone, Instagram, ArrowRight, MessageCircle } from "lucide-react";
import { useSeo, igUrl, igHandle, type SiteSettings } from "@/lib/publicContent";

const DEFAULTS = {
  eyebrow: "Parliamone",
  title: "Contatti",
  description:
    "Hai già immaginato le tue sneakers? Raccontami la tua idea oppure usa il configuratore per ottenere una stima del prezzo in tempo reale.",
  cta: "Configura la tua",
};

export default function ContactPage() {
  const settings = useOutletContext<SiteSettings | null>();

  useSeo({
    title: `Contatti — ${settings?.site_name || "Bonker"}`,
    description: "Contattami per la tua sneaker personalizzata: email, telefono e social.",
  });

  const email = settings?.contact_email;
  const phone = settings?.contact_phone;
  const waUrl = phone ? `https://wa.me/${phone.replace(/\D/g, "")}` : "";
  const instagramLink = igUrl(settings);
  const hasAny = email || phone || instagramLink;

  const eyebrow = settings?.contact_eyebrow?.trim() || DEFAULTS.eyebrow;
  const title = settings?.contact_title?.trim() || DEFAULTS.title;
  const description = settings?.contact_description?.trim() || DEFAULTS.description;
  const ctaLabel = settings?.contact_cta_label?.trim() || DEFAULTS.cta;

  return (
    <div data-testid="contact-page">
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-16 md:py-24 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-secondary" data-testid="contact-eyebrow">{eyebrow}</span>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-3" data-testid="contact-title">{title}</h1>
          <p className="text-muted-foreground mt-6 max-w-md leading-relaxed" data-testid="contact-description">{description}</p>
          {ctaLabel && (
            <Link to="/configuratore" data-testid="contact-cta-configura" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-sm px-8 py-4 mt-8 hover:-translate-y-[2px] transition-transform">
              {ctaLabel} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        <div className="space-y-4" data-testid="contact-channels">
          {email && (
            <a href={`mailto:${email}`} data-testid="contact-email-link" className="flex items-center gap-4 border border-white/10 p-5 bg-card hover:border-secondary/50 transition-colors group">
              <span className="h-11 w-11 flex items-center justify-center bg-secondary text-black shrink-0"><Mail className="h-5 w-5" /></span>
              <span><span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Email</span><span className="font-semibold">{email}</span></span>
            </a>
          )}
          {phone && (
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="contact-phone-link" className="flex items-center gap-4 border border-white/10 p-5 bg-card hover:border-secondary/50 transition-colors">
              <span className="h-11 w-11 flex items-center justify-center bg-secondary text-black shrink-0"><Phone className="h-5 w-5" /></span>
              <span><span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Telefono / WhatsApp</span><span className="font-semibold">{phone}</span></span>
            </a>
          )}
          {instagramLink && (
            <a href={instagramLink} target="_blank" rel="noreferrer" data-testid="contact-instagram-link" className="flex items-center gap-4 border border-white/10 p-5 bg-card hover:border-secondary/50 transition-colors">
              <span className="h-11 w-11 flex items-center justify-center bg-secondary text-black shrink-0"><Instagram className="h-5 w-5" /></span>
              <span><span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Instagram</span><span className="font-semibold" data-testid="contact-instagram-handle">{igHandle(settings)}</span></span>
            </a>
          )}
          {!hasAny && (
            <div className="border border-dashed border-white/15 p-8 text-center" data-testid="contact-empty">
              <MessageCircle className="h-8 w-8 text-secondary mx-auto mb-4" />
              <p className="font-display font-bold text-xl mb-2">Recapiti in arrivo</p>
              <p className="text-sm text-muted-foreground">Nel frattempo usa il configuratore per inviarmi la tua richiesta.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
